import { apiConfig, http } from "@api/base";
import { getSearchTotal } from "@api/utils";
import { sessionManager } from "@authentication/services/SessionManager";
import _isEmpty from "lodash/isEmpty";
import { generatePath } from "react-router-dom";
import { serializer } from "./serializer";

const apiPaths = {
  list: "/fines/",
  item: "/fines/:fineId",
  create: "/fines/",
  update: "/fines/:fineId",
  delete: "/fines/:fineId",
  pay: "/fines/:fineId/pay",
  waive: "/fines/:fineId/waive",
  userFines: "/fines/user/:email",
  unpaidFines: "/fines/unpaid",
  fineHistory: "/fines/:fineId/history",
  fineStats: "/fines/stats",
  sync: "/fines/sync",
  test: "/fines/test",
  resolve: "/fines/:fineId/resolve",
  cleanupReturned: "/fines/cleanup-returned-loans",
};

const get = async (fineId) => {
  const path = generatePath(apiPaths.item, { fineId: fineId });
  const response = await http.get(path);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

const getBatch = async (fineIds) => {
  if (!fineIds || fineIds.length === 0) {
    return { data: { hits: [], total: 0 } };
  }

  const ids = Array.isArray(fineIds) ? fineIds.join(",") : fineIds;
  const response = await http.get(`${apiPaths.list}batch?ids=${ids}`);

  if (response.data.status === "success" && response.data.data) {
    const finesMap = response.data.data.fines || {};

    const finesArray = Object.entries(finesMap).map(([id, fineData]) => ({
      id,
      ...serializer.fromJSON(fineData),
    }));

    response.data.hits = finesArray;
    response.data.total = finesArray.length;
    response.data.finesMap = finesMap;
  } else {
    response.data.hits = [];
    response.data.total = 0;
    response.data.finesMap = {};
  }

  return response;
};

const create = async (fineData) => {
  const currentUser = sessionManager.user;

  const backendPayload = serializer.toJSON(fineData);
  backendPayload.created_by = currentUser?.username || currentUser?.email;

  const response = await http.post(apiPaths.create, backendPayload);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

const update = async (fineId, fineData) => {
  const currentUser = sessionManager.user;
  const path = generatePath(apiPaths.update, { fineId: fineId });

  const backendPayload = serializer.toJSON(fineData);
  backendPayload.updated_by = currentUser?.username || currentUser?.email;

  const response = await http.put(path, backendPayload);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

const deleteFine = async (fineId) => {
  const path = generatePath(apiPaths.delete, { fineId: fineId });
  const response = await http.delete(path);
  return response;
};

const payFine = async (fineId, paymentData) => {
  const currentUser = sessionManager.user;
  const path = generatePath(apiPaths.pay, { fineId: fineId });

  const backendPayload = {
    payment_reference:
      paymentData.payment_reference || paymentData.paymentReference,
    or_number: paymentData.or_number || paymentData.orNumber,
    image_url: paymentData.image_url || paymentData.imageUrl,
    notes: paymentData.notes,
    updated_by: currentUser?.username || currentUser?.email,
  };

  const response = await http.post(path, backendPayload);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

const waiveFine = async (fineId, waiverData) => {
  const currentUser = sessionManager.user;
  const path = generatePath(apiPaths.waive, { fineId: fineId });

  const backendPayload = {
    notes: waiverData.notes || "Fine waived",
    updated_by: currentUser?.username || currentUser?.email,
  };

  const response = await http.post(path, backendPayload);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

const resolveFine = async (fineId, resolveData) => {
  const currentUser = sessionManager.user;
  const path = generatePath(apiPaths.resolve, { fineId: fineId });

  const backendPayload = {
    notes: resolveData.notes || "Fine resolved - item returned",
    updated_by: currentUser?.username || currentUser?.email,
  };

  const response = await http.post(path, backendPayload);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

const cleanupReturnedLoans = async (loanIds) => {
  const response = await http.post(apiPaths.cleanupReturned, {
    loan_ids: loanIds,
  });

  return response;
};

const getUserFines = async (email) => {
  const path = generatePath(apiPaths.userFines, { email: email });
  const response = await http.get(path);

  if (response.data.status === "success" && response.data.data) {
    const fines = response.data.data.fines || [];
    response.data.hits = fines.map((item) => serializer.fromJSON(item));
    response.data.total = response.data.data.total || fines.length;
    response.data.summary = response.data.data.summary || {};
  } else {
    response.data.hits = [];
    response.data.total = 0;
  }

  return response;
};

const getUnpaidFines = async () => {
  const response = await http.get(apiPaths.unpaidFines);

  if (response.data.status === "success" && response.data.data) {
    const fines = response.data.data.fines || [];
    response.data.hits = fines.map((item) => serializer.fromJSON(item));
    response.data.total = response.data.data.total || fines.length;
    response.data.summary = response.data.data.summary || {};
  } else {
    response.data.hits = [];
    response.data.total = 0;
  }

  return response;
};

const getFineHistory = async (fineId) => {
  const path = generatePath(apiPaths.fineHistory, { fineId: fineId });
  const response = await http.get(path);

  if (response.data.status === "success" && response.data.data) {
    response.data.history = response.data.data.history || [];
    response.data.total = response.data.data.total || 0;
  } else {
    response.data.history = [];
    response.data.total = 0;
  }

  return response;
};

const getFineStats = async () => {
  const response = await http.get(apiPaths.fineStats);
  return response;
};

// Sync method to synchronize loans with fine management system
const sync = async (syncData) => {
  const response = await http.post(apiPaths.sync, syncData);

  if (response.data.status === "success" && response.data.data) {
    // Process the sync response
    const processedResponse = {
      created_fines: response.data.data.created_fines || [],
      updated_fines: response.data.data.updated_fines || [],
      errors: response.data.data.errors || [],
      summary: response.data.data.summary || {
        created: 0,
        updated: 0,
        errors: 0,
        total_processed: 0,
      },
    };

    // Serialize the fine data
    processedResponse.created_fines = processedResponse.created_fines.map(
      (fine) => serializer.fromJSON(fine)
    );
    processedResponse.updated_fines = processedResponse.updated_fines.map(
      (fine) => serializer.fromJSON(fine)
    );

    response.data = processedResponse;
  }

  return response;
};

const testConnection = async () => {
  const response = await http.get(apiPaths.test);
  return response;
};

class QueryBuilder {
  constructor() {
    this.params = new URLSearchParams();
  }

  withEmail(email) {
    if (!email || _isEmpty(email)) {
      throw TypeError("Email argument missing");
    }
    this.params.set("email", email);
    return this;
  }

  withName(name) {
    if (!name || _isEmpty(name)) {
      throw TypeError("Name argument missing");
    }
    this.params.set("name", name);
    return this;
  }

  withStatus(status) {
    if (!status || _isEmpty(status)) {
      throw TypeError("Status argument missing");
    }
    this.params.set("status", status);
    return this;
  }

  withDateRange({ fromDate, toDate }) {
    if (fromDate) {
      this.params.set("start_date", fromDate);
    }
    if (toDate) {
      this.params.set("end_date", toDate);
    }
    return this;
  }

  withLoanId(loanId) {
    if (!loanId || _isEmpty(loanId)) {
      throw TypeError("Loan ID argument missing");
    }
    this.params.set("loan_id", loanId);
    return this;
  }

  withItemBarcode(itemBarcode) {
    if (!itemBarcode || _isEmpty(itemBarcode)) {
      throw TypeError("Item barcode argument missing");
    }
    this.params.set("item_barcode", itemBarcode);
    return this;
  }

  activeOnly() {
    this.params.set("is_active", "true");
    return this;
  }

  unpaidOnly() {
    this.params.set("status", "active");
    return this;
  }

  paidOnly() {
    this.params.set("status", "paid");
    return this;
  }

  waivedOnly() {
    this.params.set("status", "waived");
    return this;
  }

  withSize(size) {
    if (size > 0) {
      this.params.set("per_page", size.toString());
    }
    return this;
  }

  withPage(page = 1) {
    if (page > 0) {
      this.params.set("page", page.toString());
    }
    return this;
  }

  sortByNewest() {
    this.params.set("sort_by", "created_at");
    this.params.set("sort_order", "desc");
    return this;
  }

  sortByOldest() {
    this.params.set("sort_by", "created_at");
    this.params.set("sort_order", "asc");
    return this;
  }

  sortByAmount() {
    this.params.set("sort_by", "total_fine");
    this.params.set("sort_order", "asc");
    return this;
  }

  sortByAmountDesc() {
    this.params.set("sort_by", "total_fine");
    this.params.set("sort_order", "desc");
    return this;
  }

  sortByStartDate() {
    this.params.set("sort_by", "start_date");
    this.params.set("sort_order", "asc");
    return this;
  }

  sortByStartDateDesc() {
    this.params.set("sort_by", "start_date");
    this.params.set("sort_order", "desc");
    return this;
  }

  sortByStatus() {
    this.params.set("sort_by", "status");
    this.params.set("sort_order", "asc");
    return this;
  }

  sortByEmail() {
    this.params.set("sort_by", "email");
    this.params.set("sort_order", "asc");
    return this;
  }

  qs() {
    return this.params.toString();
  }
}

const queryBuilder = () => {
  return new QueryBuilder();
};

const list = async (queryParams = "") => {
  let url = apiPaths.list;

  if (queryParams) {
    url += `?${queryParams}`;
  }

  const response = await http.get(url);

  if (response.data.status === "success" && response.data.data) {
    const fines = response.data.data.items || response.data.data.fines || [];
    response.data.hits = fines.map((item) => serializer.fromJSON(item));
    response.data.total = response.data.data.total || fines.length;
    response.data.summary = response.data.data.summary || {};
  } else {
    response.data.hits = [];
    response.data.total = 0;
  }

  return response;
};

const count = async (query) => {
  const response = await http.get(`${apiPaths.list}?q=${query}`);
  response.data = getSearchTotal(response.data.hits);
  return response;
};

const getActiveFines = async () => {
  const queryParams = queryBuilder().activeOnly().sortByNewest().qs();

  const response = await list(queryParams);

  // Additional client-side filtering to ensure resolved/cancelled fines are excluded
  if (response.data && response.data.hits) {
    const filteredHits = response.data.hits.filter(
      (fine) => fine.status !== "resolved" && fine.status !== "cancelled"
    );

    response.data.hits = filteredHits;
    response.data.total = filteredHits.length;
  }

  return response;
};

const getFinesByUser = async (email) => {
  return await getUserFines(email);
};

const getFinesByStatus = async (status) => {
  const query = queryBuilder()
    .withStatus(status)
    .activeOnly()
    .sortByNewest()
    .qs();

  return await list(query);
};

const getFinesByDateRange = async (startDate, endDate) => {
  const query = queryBuilder()
    .withDateRange({ fromDate: startDate, toDate: endDate })
    .activeOnly()
    .sortByStartDate()
    .qs();

  return await list(query);
};

const getFinesByLoanId = async (loanId) => {
  const query = queryBuilder()
    .withLoanId(loanId)
    .activeOnly()
    .sortByNewest()
    .qs();

  return await list(query);
};

const searchFines = async (searchTerm) => {
  const query = queryBuilder()
    .withName(searchTerm)
    .activeOnly()
    .sortByNewest()
    .qs();

  return await list(query);
};

// Bulk operations
const bulkPayFines = async (fineIds, paymentData) => {
  const promises = fineIds.map((fineId) => payFine(fineId, paymentData));
  return await Promise.all(promises);
};

const bulkWaiveFines = async (fineIds, waiverData) => {
  const promises = fineIds.map((fineId) => waiveFine(fineId, waiverData));
  return await Promise.all(promises);
};

// Get all fines including resolved ones - minimal addition to existing API
const getAllFinesIncludingResolved = async () => {
  // Use existing list method but without the activeOnly filter
  const queryParams = queryBuilder().sortByNewest().qs();
  const response = await list(queryParams);

  // Return all fines without filtering out resolved/cancelled
  return response;
};

// Enhanced user fines to get all statuses for a user
const getUserFinesAllStatuses = async (email) => {
  const path = generatePath(apiPaths.userFines, { email: email });
  const response = await http.get(path);

  if (response.data.status === "success" && response.data.data) {
    const fines = response.data.data.fines || [];
    response.data.hits = fines.map((item) => serializer.fromJSON(item));
    response.data.total = response.data.data.total || fines.length;
    response.data.summary = response.data.data.summary || {};
  } else {
    response.data.hits = [];
    response.data.total = 0;
  }

  return response;
};

export const fineApi = {
  searchBaseURL: `${apiConfig.baseURL}${apiPaths.list}`,
  query: queryBuilder,
  list: list,
  get: get,
  getBatch: getBatch,
  create: create,
  update: update,
  delete: deleteFine,
  pay: payFine,
  waive: waiveFine,
  resolve: resolveFine,
  getUserFines: getUserFines,
  getUserFinesAllStatuses: getUserFinesAllStatuses,
  getUnpaidFines: getUnpaidFines,
  getFineHistory: getFineHistory,
  getFineStats: getFineStats,
  sync: sync,
  cleanupReturnedLoans: cleanupReturnedLoans,
  testConnection: testConnection,
  count: count,

  // Helper functions
  getActiveFines: getActiveFines,
  getAllFinesIncludingResolved: getAllFinesIncludingResolved,
  getFinesByUser: getFinesByUser,
  getFinesByStatus: getFinesByStatus,
  getFinesByDateRange: getFinesByDateRange,
  getFinesByLoanId: getFinesByLoanId,
  searchFines: searchFines,

  // Bulk operations
  bulkPayFines: bulkPayFines,
  bulkWaiveFines: bulkWaiveFines,

  serializer: serializer,
};
